import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuthUser } from "./useAuth";
import { useAIQuota } from "./useAIQuota";

export interface ModuleSummary {
  tldr: string;
  key_points: string[];
  concepts: { term: string; definition: string }[];
  next_steps: string[];
}

export interface ModuleSummaryRow {
  summary: ModuleSummary;
  updated_at: string;
}

export type SummaryError =
  | { kind: "quota_exceeded"; message: string }
  | { kind: "rate_limit"; message: string }
  | { kind: "no_credits"; message: string }
  | { kind: "auth"; message: string }
  | { kind: "unknown"; message: string };

export interface GenerateSummaryInput {
  moduleId: string;
  moduleTitle: string;
  category: string;
  lessonContent?: string | null;
  videoTitles?: string[];
}

export function useModuleSummary(moduleId: string | undefined) {
  const { data: user } = useAuthUser();
  const { refresh } = useAIQuota();
  const queryClient = useQueryClient();

  const queryKey = ["module-ai-summary", user?.id, moduleId];

  const query = useQuery<ModuleSummaryRow | null>({
    queryKey,
    enabled: !!user?.id && !!moduleId,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("module_ai_summaries")
        .select("summary, updated_at")
        .eq("user_id", user!.id)
        .eq("module_id", moduleId!)
        .maybeSingle();
      if (error) throw error;
      if (!data) return null;
      return { summary: data.summary as unknown as ModuleSummary, updated_at: data.updated_at };
    },
    staleTime: 60_000,
  });

  const generate = useMutation<ModuleSummary, SummaryError, GenerateSummaryInput>({
    mutationFn: async (input) => {
      const { data, error } = await supabase.functions.invoke("generate-module-summary", {
        body: input,
      });

      if (error) {
        const ctx: any = (error as any).context;
        let parsed: any = null;
        try {
          if (ctx && typeof ctx.json === "function") parsed = await ctx.json();
        } catch {
          /* ignore */
        }
        const status = ctx?.status ?? 0;
        const code = parsed?.error;
        const msg = parsed?.message || error.message || "Erro desconhecido";

        if (status === 401) throw { kind: "auth", message: "Sessão expirada. Faça login novamente." } as SummaryError;
        if (status === 429 && code === "quota_exceeded")
          throw { kind: "quota_exceeded", message: msg } as SummaryError;
        if (status === 429) throw { kind: "rate_limit", message: msg } as SummaryError;
        if (status === 402) throw { kind: "no_credits", message: msg } as SummaryError;
        throw { kind: "unknown", message: msg } as SummaryError;
      }

      return (data as { summary: ModuleSummary }).summary;
    },
    onSuccess: () => {
      refresh();
      queryClient.invalidateQueries({ queryKey });
    },
  });

  return { query, generate };
}
